<template>
    <div>
        <p v-if="!forOneReport" class="small text-muted">
            Every question asked of the report generator, and every report run, exported, saved or
            deleted - by everyone, newest first. Kept on the server, so a colleague's activity is here too.
        </p>
        <p v-else class="small text-muted">
            Everything done with this report since it was saved - runs, exports, renames and setting
            changes - by everyone, newest first.
        </p>

        <p-card>
            <p-table :items="entries" :fields="fields" :pagination="false" :per-page="0" :enable-row-selection="false" :show-record-count="false" :is-busy="isBusy">
                <template v-slot:cell(createdOn)="{ item }">{{ when(item.createdOn) }}</template>
                <template v-slot:cell(action)="{ item }">
                    <span :class="`badge bg-${actionVariant(item.action)}`">{{ actionLabel(item.action) }}</span>
                </template>
                <!-- The saved report if it still exists, else the question that was asked. -->
                <template v-slot:cell(what)="{ item }">
                    <router-link v-if="reportFor(item)" :to="`/reports/ai/${reportFor(item).id}`">
                        {{ reportFor(item).name }}
                    </router-link>
                    <span v-else-if="item.question">{{ item.question }}</span>
                    <span v-else-if="item.reportDefinitionId" class="text-muted">A report that has since been deleted</span>
                    <span v-else class="text-muted">—</span>
                </template>
                <template v-slot:cell(rowCount)="{ item }">
                    <span v-if="item.rowCount !== null && item.rowCount !== undefined">{{ item.rowCount }}</span>
                    <span v-else class="text-muted">—</span>
                </template>
            </p-table>

            <p v-if="!entries.length && !isBusy" class="mb-0 text-muted">
                {{ forOneReport ? 'No activity for this report yet.' : 'No report activity yet.' }}
            </p>
            <ServerPager
                :total="total"
                :page="page"
                :per-page="perPage"
                @page="load($event)"
                @per-page="changePerPage($event)" />
        </p-card>
    </div>
</template>

<script>
// The report generator's shared activity log (GET reportagent/activity), or - given a
// reportDefinitionId - one saved report's history.
import { listActivity } from '@/services/reportAgentApi';
import { useAiReportsStore } from '@/stores/aiReports';
import ServerPager from '@/views/Reports/Agent/ServerPager.vue';

const PER_PAGE = 25;

const ACTIONS = {
    Resolved: { label: 'Asked', variant: 'primary' },
    NoMatch: { label: 'Not understood', variant: 'secondary' },
    Unavailable: { label: 'AI unavailable', variant: 'warning' },
    Run: { label: 'Ran', variant: 'success' },
    Exported: { label: 'Exported', variant: 'info' },
    Saved: { label: 'Saved', variant: 'success' },
    Updated: { label: 'Updated', variant: 'info' },
    Deleted: { label: 'Deleted', variant: 'danger' },
};

export default {
    name: 'ReportActivity',
    components: { ServerPager },
    props: {
        // Set on a saved report's page: only that report's history.
        reportDefinitionId: { type: Number, default: null },
    },
    data() {
        return {
            entries: [],
            total: 0,
            page: 1,
            perPage: PER_PAGE,
            isBusy: false,
        };
    },
    computed: {
        store() {
            return useAiReportsStore();
        },
        // Any id at all means one report's history - a bad id must never widen this to
        // everyone's activity, so this does not test the id's value.
        forOneReport() {
            return this.reportDefinitionId !== null && this.reportDefinitionId !== undefined;
        },
        validReportId() {
            return Number.isInteger(this.reportDefinitionId) && this.reportDefinitionId > 0;
        },
        // On one report's page every row is that report, so the column naming it goes.
        fields() {
            return [
                { key: 'createdOn', label: 'When', class: 'tcw150' },
                { key: 'userName', label: 'Who', class: 'tcw150' },
                { key: 'action', label: 'Action', class: 'tcw150' },
                ...(this.forOneReport ? [] : [{ key: 'what', label: 'Question or report' }]),
                { key: 'rowCount', label: 'Rows', class: 'tcw100' },
            ];
        },
    },
    async created() {
        if (!this.store.loaded)
            this.store.load();

        await this.load(1);
    },
    methods: {
        async load(page) {
            // A report id that is not a real id has no history to show; ask for nothing.
            if (this.forOneReport && !this.validReportId) {
                this.entries = [];
                this.total = 0;
                this.page = 1;
                return;
            }

            this.isBusy = true;
            try {
                const paging = { currentPage: page, perPage: this.perPage };
                if (this.forOneReport)
                    paging.reportDefinitionId = this.reportDefinitionId;

                const data = await listActivity(paging);
                this.entries = data.data;
                this.total = data.count;
                this.page = page;
            } catch {
                // The global handler shows why.
            } finally {
                this.isBusy = false;
            }
        },
        // A new page size starts again from the first page.
        changePerPage(perPage) {
            this.perPage = perPage;
            this.load(1);
        },
        reportFor(entry) {
            return entry.reportDefinitionId ? this.store.getById(entry.reportDefinitionId) || null : null;
        },
        actionLabel(action) {
            return (ACTIONS[action] && ACTIONS[action].label) || action;
        },
        actionVariant(action) {
            return (ACTIONS[action] && ACTIONS[action].variant) || 'secondary';
        },
        when(iso) {
            return iso ? new Date(iso).toLocaleString() : '';
        },
    },
};
</script>
